import validator from "validator"
import { isValidPhoneNumber } from "libphonenumber-js"
import { nameHasComma, NAME_COMMA_ERROR } from "./nameCommaValidation.js"
import { COUNTRY_CANADA, COUNTRY_JAPAN, COUNTRY_UNITED_STATES } from "../utils/countries.js"
import { getCountriesList, getStatesListForCountryName } from "../countryStateData.js"

/** Same messages / libs as OMS V1 OrderForm. */
const EMAIL_INVALID_MESSAGE = "Must be a valid email address"
const PHONE_INVALID_MESSAGE = "Must be a valid phone number"

function isValidEmail(value) {
    return validator.isEmail(t(value))
}

/** Canada default / NANP — same as V1 `isValidPhoneNumber(value, 'CA')`. */
function isValidPhone(value) {
    return isValidPhoneNumber(t(value), "CA")
}

/** @typedef {{ section: number | "dropoff", field: string, message: string, domId: string, label: string }} OrderFormError */

export const ORDER_FORM_SECTION = {
    DROPOFF: /** @type {const} */ ("dropoff"),
    CONTACT: 0,
    DELIVERY: 1,
    SHIPPING: 2,
    BILLING: 3,
    PROCESSING: 4,
    REVIEW: 5,
}

const SECTION_LABELS = {
    dropoff: "Drop-off",
    0: "Contact Information",
    1: "Delivery Method",
    2: "Shipping Information",
    3: "Billing Information",
    4: "Processing Details",
    5: "Review & Submit",
}

function t(v) {
    return String(v ?? "").trim()
}

function empty(v) {
    return t(v) === ""
}

/**
 * @param {Partial<OrderFormError> & { section: number | "dropoff", field: string, message: string, domId: string }} partial
 * @returns {OrderFormError}
 */
function err(partial) {
    return {
        label: partial.label ?? partial.field,
        ...partial,
    }
}

function knownCountryNames() {
    return new Set((getCountriesList() ?? []).map((c) => String(c?.name ?? "")))
}

function countryHasStates(countryName) {
    const states = getStatesListForCountryName(String(countryName ?? ""))
    return Array.isArray(states) && states.length > 0
}

function isValidContactMethod(raw) {
    const m = String(raw ?? "").toLowerCase().trim()
    return m === "email" || m === "phone" || m === "text"
}

function unitTypeApi(unit) {
    if (unit === "Whole Fish") return "whole"
    if (unit === "Individual Pieces") return "pieces"
    return null
}

/**
 * @param {OrderFormError[]} errors
 * @param {number | "dropoff"} section
 * @returns {Record<string, string>}
 */
export function fieldErrorsForSection(errors, section) {
    /** @type {Record<string, string>} */
    const map = {}
    for (const e of errors) {
        if (e.section !== section) continue
        if (map[e.field] == null) {
            map[e.field] = e.message
        }
    }
    return map
}

/**
 * @param {OrderFormError[]} errors
 * @returns {Map<number | "dropoff", OrderFormError[]>}
 */
export function groupErrorsBySection(errors) {
    /** @type {Map<number | "dropoff", OrderFormError[]>} */
    const map = new Map()
    for (const e of errors) {
        const list = map.get(e.section) ?? []
        list.push(e)
        map.set(e.section, list)
    }
    return map
}

export function sectionTitleForErrorSection(section) {
    return SECTION_LABELS[section] ?? String(section)
}

/** Terms / signature — only surface after Submit, not when Review first enters view. */
export function isReviewAcknowledgementError(error) {
    return (
        error?.section === ORDER_FORM_SECTION.REVIEW &&
        (error?.field === "termsAccepted" || error?.field === "signature")
    )
}

/**
 * @param {OrderFormError[] | null | undefined} errors
 * @returns {OrderFormError[]}
 */
export function withoutReviewAcknowledgementErrors(errors) {
    return (errors ?? []).filter((e) => !isReviewAcknowledgementError(e))
}

/**
 * @param {{
 *   contact: Record<string, unknown>,
 *   delivery: { method?: string, pickupLocationId?: number | null, holdForPickup?: boolean },
 *   shipping: Record<string, unknown>,
 *   billing: Record<string, unknown>,
 *   dropoff: Record<string, unknown>,
 *   processing: Record<string, unknown>,
 *   signatureBlob?: Blob | null,
 *   termsAccepted?: boolean,
 *   requireSignature?: boolean,
 *   processes?: Array<{ id?: unknown, frozen?: boolean }>,
 *   speciesOptions?: Array<{ id?: unknown }>,
 *   optionsDataset?: Array<{ id?: unknown }>,
 *   pickupLocations?: Array<{ id?: unknown }>,
 *   dropoffLocations?: Array<{ id?: unknown, is_lodge?: boolean, isLodge?: boolean }>,
 *   allowLodgeDropoff?: boolean,
 * }} model
 * @returns {OrderFormError[]}
 */
export function validateOrderForm(model) {
    /** @type {OrderFormError[]} */
    const errors = []
    const {
        contact,
        delivery,
        shipping,
        billing,
        dropoff,
        processing,
        signatureBlob = null,
        termsAccepted = false,
        requireSignature = true,
        processes = [],
        speciesOptions = [],
        optionsDataset = [],
        pickupLocations = [],
        dropoffLocations = [],
        allowLodgeDropoff = false,
    } = model

    const isPickupMethod = String(delivery?.method ?? "") === "pickup"
    const holdForPickup = isPickupMethod && Boolean(delivery?.holdForPickup)
    const isPickup =
        isPickupMethod &&
        delivery?.pickupLocationId != null

    // --- Drop-off (LocationID) ---
    if (dropoff?.locationId == null) {
        errors.push(err({
            section: ORDER_FORM_SECTION.DROPOFF,
            field: "locationId",
            label: "Drop-off Location",
            message: "Select a drop-off location",
            domId: "dropoff-location",
        }))
    } else {
        const loc = (dropoffLocations ?? []).find(
            (l) => Number(l.id) === Number(dropoff.locationId)
        )
        if (!loc) {
            errors.push(err({
                section: ORDER_FORM_SECTION.DROPOFF,
                field: "locationId",
                label: "Drop-off Location",
                message: "Drop-off location is invalid",
                domId: "dropoff-location",
            }))
        } else if (!allowLodgeDropoff && (loc.is_lodge || loc.isLodge)) {
            errors.push(err({
                section: ORDER_FORM_SECTION.DROPOFF,
                field: "locationId",
                label: "Drop-off Location",
                message: "Drop-off location is invalid",
                domId: "dropoff-location",
            }))
        }
    }
    if (empty(dropoff?.date)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.DROPOFF,
            field: "date",
            label: "Drop-off Date",
            message: "Enter a drop-off date",
            domId: "dropoff-date",
        }))
    }

    // --- Contact ---
    if (empty(contact?.firstName)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "firstName",
            label: "First Name",
            message: "Required",
            domId: "new-order-contact-first-name",
        }))
    } else if (nameHasComma(contact.firstName)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "firstName",
            label: "First Name",
            message: NAME_COMMA_ERROR,
            domId: "new-order-contact-first-name",
        }))
    }
    if (empty(contact?.lastName)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "lastName",
            label: "Last Name",
            message: "Required",
            domId: "new-order-contact-last-name",
        }))
    } else if (nameHasComma(contact.lastName)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "lastName",
            label: "Last Name",
            message: NAME_COMMA_ERROR,
            domId: "new-order-contact-last-name",
        }))
    }
    if (empty(contact?.email)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "email",
            label: "Email",
            message: "Required",
            domId: "new-order-contact-email",
        }))
    } else if (!isValidEmail(contact.email)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "email",
            label: "Email",
            message: EMAIL_INVALID_MESSAGE,
            domId: "new-order-contact-email",
        }))
    }
    if (empty(contact?.phone)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "phone",
            label: "Phone",
            message: "Required",
            domId: "new-order-contact-phone",
        }))
    } else if (!isValidPhone(contact.phone)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "phone",
            label: "Phone",
            message: PHONE_INVALID_MESSAGE,
            domId: "new-order-contact-phone",
        }))
    }
    if (empty(contact?.licenseNumber)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "licenseNumber",
            label: "Fishing License Number",
            message: "Required",
            domId: "new-order-contact-license",
        }))
    }
    if (!isValidContactMethod(contact?.contact_method)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "contact_method",
            label: "Preferred Contact Method",
            message: "Select a valid contact method",
            domId: "new-order-contact-method",
        }))
    }
    if (contact?.updateCustomerFields === true && contact?.customerId == null) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "updateCustomerFields",
            label: "Update Customer",
            message: "Cannot update customer without a selected customer",
            domId: "contact-update-customer-fields",
        }))
    }
    const prepaid = Number(contact?.prepaidAmount ?? 0)
    if (contact?.prepaidAmount != null && contact.prepaidAmount !== "" && Number.isFinite(prepaid) && prepaid < 0) {
        errors.push(err({
            section: ORDER_FORM_SECTION.CONTACT,
            field: "prepaidAmount",
            label: "Prepaid Amount",
            message: "Prepaid amount cannot be negative",
            domId: "contact-prepaid-amount",
        }))
    }

    // License images for non-CA/US ship (existing UI rule)
    const shipCountry = t(shipping?.country)
    const isNonCanadaOrUs =
        shipCountry !== "" &&
        shipCountry !== COUNTRY_CANADA &&
        shipCountry !== COUNTRY_UNITED_STATES &&
        !isPickup
    if (isNonCanadaOrUs) {
        const hasFront =
            contact?.licenseFileFront instanceof File || Boolean(contact?.licenseFrontImg)
        const hasBack =
            contact?.licenseFileBack instanceof File || Boolean(contact?.licenseBackImg)
        if (!hasFront || !hasBack) {
            errors.push(err({
                section: ORDER_FORM_SECTION.CONTACT,
                field: "licensePhotos",
                label: "License Photos",
                message: "Front and back fishing license images are required for this shipping country",
                domId: "new-order-contact-license-photos",
            }))
        }
    }

    // --- Delivery ---
    // Pickup location is only required when "Pick up at a location" is selected.
    if (isPickupMethod) {
        if (delivery?.pickupLocationId == null) {
            errors.push(err({
                section: ORDER_FORM_SECTION.DELIVERY,
                field: "pickupLocationId",
                label: "Pickup Location",
                message: "Select a pickup location",
                domId: "delivery-pickup-location",
            }))
        } else {
            const ok = (pickupLocations ?? []).some(
                (l) => Number(l.id) === Number(delivery.pickupLocationId)
            )
            if (!ok) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.DELIVERY,
                    field: "pickupLocationId",
                    label: "Pickup Location",
                    message: "Pickup location is invalid",
                    domId: "delivery-pickup-location",
                }))
            }
        }
    }

    // Containers live on Delivery only for hold-for-pickup; otherwise on Processing.
    const boxesRaw = processing?.containers
    const boxesNum = Number(boxesRaw)
    const boxesInvalid = empty(boxesRaw) || !Number.isFinite(boxesNum) || boxesNum < 1
    if (holdForPickup && boxesInvalid) {
        errors.push(err({
            section: ORDER_FORM_SECTION.DELIVERY,
            field: "containers",
            label: "Number of Containers",
            message: "Enter at least 1 container",
            domId: "processing-containers",
        }))
    }

    // --- Shipping address (when not pickup) ---
    if (!isPickup) {
        pushAddressErrors(errors, {
            section: ORDER_FORM_SECTION.SHIPPING,
            prefix: "shipping",
            addr: shipping,
            requirePerson: true,
            requirePhone: true,
            requireEmail: false,
        })
    }

    // --- Billing address (always) ---
    if (billing?.useShippingAddress && isPickup) {
        // Backend forbids bill-same-as-ship with pickup; UI should have independent billing.
        // If somehow still set, require billing fields via the normal path below after treating as not same.
    }
    const billSameAsShip = Boolean(billing?.useShippingAddress) && !isPickup
    if (!billSameAsShip) {
        pushAddressErrors(errors, {
            section: ORDER_FORM_SECTION.BILLING,
            prefix: "billing",
            addr: billing,
            requirePerson: true,
            requirePhone: true,
            requireEmail: true,
        })
    }

    // --- Processing (when not hold for pickup) ---
    if (!holdForPickup) {
        const rows = processing?.instructions ?? []
        if (rows.length === 0) {
            errors.push(err({
                section: ORDER_FORM_SECTION.PROCESSING,
                field: "instructions",
                label: "Processing Details",
                message: "Add at least one processing line",
                domId: "processing-add-instruction",
            }))
        }

        const speciesIds = new Set(
            (speciesOptions ?? [])
                .map((s) => (s?.id == null ? null : Number(s.id)))
                .filter((id) => Number.isFinite(id))
        )
        const processById = new Map()
        for (const p of processes ?? []) {
            const id = p?.id == null ? null : Number(p.id)
            if (Number.isFinite(id)) processById.set(id, p)
        }

        /** @type {Map<string, number[]>} */
        const comboRows = new Map()
        rows.forEach((row, i) => {
            const speciesId = row?.speciesId == null ? null : Number(row.speciesId)
            const processId = row?.processId == null ? null : Number(row.processId)
            const qty = Number(row?.quantity)
            const unitApi = unitTypeApi(row?.unit)

            if (speciesId == null || !Number.isFinite(speciesId)) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.speciesId`,
                    label: `Species (Process #${i + 1})`,
                    message: "Select a species",
                    domId: `processing-species-${i}`,
                }))
            } else if (speciesIds.size > 0 && !speciesIds.has(speciesId)) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.speciesId`,
                    label: `Species (Process #${i + 1})`,
                    message: "Species is invalid",
                    domId: `processing-species-${i}`,
                }))
            }

            if (processId == null || !Number.isFinite(processId)) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.processId`,
                    label: `Processing Method (Process #${i + 1})`,
                    message: "Select a processing method",
                    domId: `processing-method-${i}`,
                }))
            } else if (processById.size > 0 && !processById.has(processId)) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.processId`,
                    label: `Processing Method (Process #${i + 1})`,
                    message: "Processing method is invalid",
                    domId: `processing-method-${i}`,
                }))
            } else if (
                shipCountry === COUNTRY_JAPAN &&
                Boolean(processById.get(processId)?.frozen)
            ) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.processId`,
                    label: `Processing Method (Process #${i + 1})`,
                    message: "Frozen processing is not allowed for Japan shipping",
                    domId: `processing-method-${i}`,
                }))
            }

            if (!Number.isFinite(qty) || qty < 1) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.quantity`,
                    label: `Quantity (Process #${i + 1})`,
                    message: "Enter a quantity of at least 1",
                    domId: `processing-quantity-${i}`,
                }))
            }

            if (unitApi == null) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.unit`,
                    label: `Unit (Line ${i + 1})`,
                    message: "Select a unit type",
                    domId: `processing-unit-${i}`,
                }))
            }

            const notes = String(row?.additionalNotes ?? "")
            if (notes.length > 255) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.additionalNotes`,
                    label: `Notes (Line ${i + 1})`,
                    message: "Notes must be 255 characters or fewer",
                    domId: `processing-notes-${i}`,
                }))
            }

            if (Number.isFinite(speciesId) && Number.isFinite(processId)) {
                const key = `${speciesId}|${processId}`
                const list = comboRows.get(key) ?? []
                list.push(i)
                comboRows.set(key, list)
            }
        })

        for (const [, idxs] of comboRows) {
            if (idxs.length < 2) continue
            for (const i of idxs) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `instructions.${i}.duplicate`,
                    label: `Processing Line ${i + 1}`,
                    message: "Duplicate species and processing method combination",
                    domId: `processing-species-${i}`,
                }))
            }
        }

        // Options: only validate entries that are present/true in form state when sent
        // Payload only sends selected options; invalid IDs shouldn't appear from UI.
        const selected = processing?.options ?? {}
        const optionIds = new Set(
            (optionsDataset ?? [])
                .map((o) => (o?.id == null ? null : Number(o.id)))
                .filter((id) => Number.isFinite(id))
        )
        for (const [rawId, on] of Object.entries(selected)) {
            if (!on) continue
            const oid = Number(rawId)
            if (!Number.isFinite(oid) || (optionIds.size > 0 && !optionIds.has(oid))) {
                errors.push(err({
                    section: ORDER_FORM_SECTION.PROCESSING,
                    field: `options.${rawId}`,
                    label: "Processing Option",
                    message: "Processing option is invalid",
                    domId: "processing-options",
                }))
            }
        }

        const weightRaw = processing?.totalWeight
        const weightNum = Number(weightRaw)
        if (empty(weightRaw) || !Number.isFinite(weightNum) || weightNum <= 0) {
            errors.push(err({
                section: ORDER_FORM_SECTION.PROCESSING,
                field: "totalWeight",
                label: "Weight",
                message: "Enter a total weight greater than 0",
                domId: "processing-total-weight",
            }))
        }

        if (boxesInvalid) {
            errors.push(err({
                section: ORDER_FORM_SECTION.PROCESSING,
                field: "containers",
                label: "Containers",
                message: "Enter at least 1 container",
                domId: "processing-containers",
            }))
        }
    }

    // --- Review UI requirements (not CreateOrderV2, but block submit today) ---
    if (!termsAccepted) {
        errors.push(err({
            section: ORDER_FORM_SECTION.REVIEW,
            field: "termsAccepted",
            label: "Terms",
            message: "Accept the terms to submit",
            domId: "review-terms",
        }))
    }
    if (requireSignature && (!(signatureBlob instanceof Blob) || signatureBlob.size <= 0)) {
        errors.push(err({
            section: ORDER_FORM_SECTION.REVIEW,
            field: "signature",
            label: "Signature",
            message: "Add your signature",
            domId: "review-signature",
        }))
    }

    return errors
}

/**
 * @param {OrderFormError[]} errors
 * @param {{
 *   section: number,
 *   prefix: string,
 *   addr: Record<string, unknown>,
 *   requirePerson: boolean,
 *   requirePhone: boolean,
 *   requireEmail: boolean,
 * }} opts
 */
function pushAddressErrors(errors, opts) {
    const { section, prefix, addr, requirePerson, requirePhone, requireEmail } = opts
    const countries = knownCountryNames()

    if (requirePerson) {
        if (empty(addr?.firstName)) {
            errors.push(err({
                section,
                field: "firstName",
                label: "First Name",
                message: "Required",
                domId: `${prefix}-first-name`,
            }))
        } else if (nameHasComma(addr.firstName)) {
            errors.push(err({
                section,
                field: "firstName",
                label: "First Name",
                message: NAME_COMMA_ERROR,
                domId: `${prefix}-first-name`,
            }))
        }
        if (empty(addr?.lastName)) {
            errors.push(err({
                section,
                field: "lastName",
                label: "Last Name",
                message: "Required",
                domId: `${prefix}-last-name`,
            }))
        } else if (nameHasComma(addr.lastName)) {
            errors.push(err({
                section,
                field: "lastName",
                label: "Last Name",
                message: NAME_COMMA_ERROR,
                domId: `${prefix}-last-name`,
            }))
        }
    } else {
        if (!empty(addr?.firstName) && nameHasComma(addr.firstName)) {
            errors.push(err({
                section,
                field: "firstName",
                label: "First Name",
                message: NAME_COMMA_ERROR,
                domId: `${prefix}-first-name`,
            }))
        }
        if (!empty(addr?.lastName) && nameHasComma(addr.lastName)) {
            errors.push(err({
                section,
                field: "lastName",
                label: "Last Name",
                message: NAME_COMMA_ERROR,
                domId: `${prefix}-last-name`,
            }))
        }
    }

    if (empty(addr?.country)) {
        errors.push(err({
            section,
            field: "country",
            label: "Country",
            message: "Required",
            domId: `${prefix}-country`,
        }))
    } else if (countries.size > 0 && !countries.has(String(addr.country))) {
        errors.push(err({
            section,
            field: "country",
            label: "Country",
            message: "Country is invalid",
            domId: `${prefix}-country`,
        }))
    }

    if (empty(addr?.streetAddress)) {
        errors.push(err({
            section,
            field: "streetAddress",
            label: "Street Address",
            message: "Required",
            domId: `${prefix}-street`,
        }))
    }
    if (empty(addr?.city)) {
        errors.push(err({
            section,
            field: "city",
            label: "City",
            message: "Required",
            domId: `${prefix}-city`,
        }))
    }
    // Backend always requires State; also require when country has a state list.
    if (empty(addr?.province)) {
        errors.push(err({
            section,
            field: "province",
            label: "Province / State",
            message: "Required",
            domId: `${prefix}-province`,
        }))
    } else if (
        !empty(addr?.country) &&
        countryHasStates(String(addr.country))
    ) {
        const states = getStatesListForCountryName(String(addr.country)) ?? []
        const code = String(addr.province)
        const ok = states.some(
            (s) => String(s.state_code) === code || String(s.name) === code
        )
        if (!ok) {
            errors.push(err({
                section,
                field: "province",
                label: "Province / State",
                message: "Province / state is invalid",
                domId: `${prefix}-province`,
            }))
        }
    }
    if (empty(addr?.postalCode)) {
        errors.push(err({
            section,
            field: "postalCode",
            label: "Postal Code",
            message: "Required",
            domId: `${prefix}-postal`,
        }))
    }
    if (requirePhone && empty(addr?.phone)) {
        errors.push(err({
            section,
            field: "phone",
            label: "Phone",
            message: "Required",
            domId: `${prefix}-phone`,
        }))
    } else if (!empty(addr?.phone) && !isValidPhone(addr.phone)) {
        errors.push(err({
            section,
            field: "phone",
            label: "Phone",
            message: PHONE_INVALID_MESSAGE,
            domId: `${prefix}-phone`,
        }))
    }
    if (requireEmail && empty(addr?.email)) {
        errors.push(err({
            section,
            field: "email",
            label: "Email",
            message: "Required",
            domId: `${prefix}-email`,
        }))
    } else if (!empty(addr?.email) && !isValidEmail(addr.email)) {
        errors.push(err({
            section,
            field: "email",
            label: "Email",
            message: EMAIL_INVALID_MESSAGE,
            domId: `${prefix}-email`,
        }))
    }
}

/**
 * @param {OrderFormError[]} errors
 * @param {number | "dropoff"} section
 */
export function errorsForSection(errors, section) {
    return errors.filter((e) => e.section === section)
}

/**
 * @param {string[]} items
 */
function joinFriendlyList(items) {
    if (items.length === 0) return ""
    if (items.length === 1) return items[0]
    if (items.length === 2) return `${items[0]} and ${items[1]}`
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

/**
 * @param {OrderFormError} error
 * @returns {"missing" | "invalid"}
 */
function categorizeOrderFormIssue(error) {
    const message = String(error?.message ?? "")
    if (message === "Required" || /\brequired\b/i.test(message)) return "missing"
    if (/^(Select|Enter|Add)\b/i.test(message) && !/\bvalid\b/i.test(message) && !/\binvalid\b/i.test(message)) {
        return "missing"
    }
    return "invalid"
}

/**
 * Friendly accordion status for a section's challenged errors.
 * E.g. "Missing: First Name, Last Name, and Phone. Invalid: Email. (4 issues)"
 *
 * @param {Array<{ label?: string, message?: string, field?: string } | null | undefined> | null | undefined} errors
 */
export function formatOrderFormSectionIssueSummary(errors) {
    const list = (errors ?? []).filter(Boolean)
    if (list.length === 0) return ""

    /** @type {string[]} */
    const missing = []
    /** @type {string[]} */
    const invalid = []
    const seenMissing = new Set()
    const seenInvalid = new Set()

    for (const error of list) {
        const label = String(error.label ?? error.field ?? "").trim() || "Field"
        const category = categorizeOrderFormIssue(/** @type {OrderFormError} */(error))
        if (category === "missing") {
            if (!seenMissing.has(label)) {
                seenMissing.add(label)
                missing.push(label)
            }
        } else if (!seenInvalid.has(label)) {
            seenInvalid.add(label)
            invalid.push(label)
        }
    }

    /** @type {string[]} */
    const parts = []
    if (missing.length > 0) {
        parts.push(`Missing: ${joinFriendlyList(missing)}`)
    }
    if (invalid.length > 0) {
        parts.push(`Invalid: ${joinFriendlyList(invalid)}`)
    }

    // Multi-sentence only: join with ". " and end with a period. Single sentence stays bare.
    let summary = parts.join(". ")
    if (parts.length > 1 && summary && !/[.!?]$/.test(summary)) summary += "."

    const count = list.length
    const suffix = `(${count} issue${count === 1 ? "" : "s"})`
    return summary ? `${summary} ${suffix}` : suffix
}

/**
 * First error in form order for focus/scroll.
 * @param {OrderFormError[]} errors
 */
export function firstOrderFormError(errors) {
    const order = ["dropoff", 0, 1, 2, 3, 4, 5]
    for (const section of order) {
        const hit = errors.find((e) => e.section === section)
        if (hit) return hit
    }
    return errors[0] ?? null
}
